<template>
  <ModalWrapperComponent
    dialog-name="VerifyPrimaryEmailAddressCtnComponent"
    :show-dialog="showDialog"
    @close-modal="close"
    @pre-open="beforeOpen"
  >
    <template #header>
      {{ tn("headerH1") }}
    </template>
    <template #content>
      <ul>
        <li>
          <i18n-t keypath="VerifyPrimaryEmailAddressCtnComponent.line1" tag="p" scope="global"></i18n-t>
        </li>
        <li>
          <i18n-t keypath="VerifyPrimaryEmailAddressCtnComponent.line2" tag="p">
            <template #email>
              <strong v-if="pendingVerificationEmail">{{ pendingVerificationEmail }}</strong>
              <strong v-else>{{ eMail }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="VerifyPrimaryEmailAddressCtnComponent.line3" tag="p">
            <template #time>
              <strong class="danger-text">{{ tn("time") }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <button
            id="btnSendMail"
            type="button"
            class="btn-outline-primary btn-action"
            :disabled="resendMailSent || isSaving"
            @click="sendAnotherEmail()"
          >
            <spinner :is-spinning="isSaving" />
            <span v-if="!resendMailSent">{{ tn("sendAnotherEmail") }}</span>
            <span v-else>{{ tn("emailSent") }}</span>
          </button>
        </li>
      </ul>
    </template>
    <template #footer>
      <button id="btnClose" type="button" class="btn-link mr" @click="close()">{{ tn("cancel") }}</button>
      <button id="btnContinue" type="button" class="btn-primary" @click="handleContinue()">
        {{ tn("continue") }}
      </button>
    </template>
  </ModalWrapperComponent>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { unifiedApi } from "@/common";
import { t } from "@/i18n";
import { reloadSubscriberInfo } from "@/common/reloadSubscriberInfo";
import { logEvent } from "@/common/logger";
import ModalWrapperComponent from "@/components/shared/dialogs/ModalWrapper.vue";
import { useNotificationsStore } from "@/stores/notifications";
import Spinner from "@/components/shared/Spinner.vue";
import { useUserStore } from "@/stores/user";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import { getPendingEmailVerification, pendingVerificationEmail } from "@/common/pendingEmailVerification";

const componentName = "VerifyPrimaryEmailAddressCtnComponent";

export default defineComponent({
  name: componentName,
  components: { ModalWrapperComponent, Spinner },
  props: {
    email: {
      type: String,
      required: true,
    },
    showDialog: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["close-modal", "save-modal"],
  setup(props, context) {
    logEvent("created", componentName, props.email);
    const notificationsStore = useNotificationsStore();
    const eMail = ref<string>(props.email);
    const resendMailSent = ref(false);
    const isSaving = ref(false);

    const userStore = useUserStore();

    const user = computed(() => {
      return userStore.currentUser;
    });

    const hasValidatedEmail = computed(() => {
      return user.value?.subscriberInfo?.hasValidatedEmail;
    });

    onMounted(async () => {
      await getPendingEmailVerification();
    });

    function beforeOpen() {
      notificationsStore.clearNotifications();
      eMail.value = props.email;
      resendMailSent.value = false;
      isSaving.value = false;
    }

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${componentName}.${v}`, params);
    }

    async function sendAnotherEmail() {
      isSaving.value = true;
      notificationsStore.clearNotifications();
      try {
        const emailUpdateResponse = (await unifiedApi.resendVerificationEmail()).data;
        if (emailUpdateResponse !== "") {
          logEvent("resend another email failed : ", emailUpdateResponse);
        }
      } catch (error) {
        logEvent(`Resulted in not OK ${error}`, `${componentName}/sendAnotherEmail`);
        handleApiError(error as AxiosError);
      }
      resendMailSent.value = true;
      isSaving.value = false;
    }

    async function handleContinue() {
      notificationsStore.clearNotifications();
      await reloadSubscriberInfo();
      if (!hasValidatedEmail.value) {
        notificationsStore.addNotification({ type: "EmailVerificationIncomplete" });
        return;
      } else {
        context.emit("save-modal", "verificationCompleted");
      }
    }

    function close() {
      context.emit("close-modal");
    }

    //Expose to template
    return {
      close,
      eMail,
      beforeOpen,
      handleContinue,
      sendAnotherEmail,
      resendMailSent,
      isSaving,
      tn,
      pendingVerificationEmail,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";
</style>
